/**
 * HtmlEmbed
 * ---------
 * Renders admin-authored HTML that may contain executing <script> tags
 * (Cognito Forms, Calendly, Typeform, etc.). Plain
 * `dangerouslySetInnerHTML` parses scripts into the DOM but they never
 * run — that's a browser security spec, not a React bug. To make them
 * execute we have to recreate each <script> via document.createElement
 * and append it to the live DOM ourselves.
 *
 * Usage:
 *   <HtmlEmbed html={embed} testId="invest-cognito-embed" />
 *
 * Notes:
 *   - Inputs are workspace-admin authored. We don't sanitize here — the
 *     admin already passed the auth gate to save the embed. This is the
 *     same trust model the existing `dangerouslySetInnerHTML` calls use.
 *   - We re-render scripts every time `html` changes. Re-mounts cleanly
 *     replace the embed instead of stacking multiple copies.
 *   - Handles BOTH the script-tag variant (Cognito seamless) and the
 *     iframe variant — iframes work fine via innerHTML, scripts get the
 *     rebuild treatment.
 */
import { useEffect, useRef } from "react";

export default function HtmlEmbed({ html, className, testId }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Clear any previous render (handles the case where the parent
    // updates `html` — without this we'd stack embeds).
    el.innerHTML = "";

    if (!html || typeof html !== "string") return;

    // Parse the supplied HTML in a detached document so we can pull
    // <script> nodes out cleanly without their text triggering anything
    // in the parse step.
    const tpl = document.createElement("template");
    tpl.innerHTML = html;

    // Walk the parsed tree and clone everything into our live container.
    // Element nodes are passed through unchanged EXCEPT <script>, which
    // we rebuild via document.createElement so the browser actually
    // executes it. innerHTML-injected scripts never execute — that's
    // the whole reason this component exists.
    const walk = (parent, source) => {
      Array.from(source.childNodes).forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === "SCRIPT") {
          const s = document.createElement("script");
          // Copy ALL attributes (src, async, defer, type, data-*…).
          Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          // Inline script body, if any.
          if (node.textContent) s.textContent = node.textContent;
          parent.appendChild(s);
        } else {
          const cloned = node.cloneNode(false);
          parent.appendChild(cloned);
          if (node.childNodes && node.childNodes.length) {
            walk(cloned, node);
          }
        }
      });
    };

    walk(el, tpl.content);
  }, [html]);

  return (
    <div ref={containerRef} className={className} data-testid={testId} />
  );
}
